import { normalizeMarkdownContent } from "@/utility/normalizeMarkdownContent";
import { normalizeTableCellListContent } from "@/utility/tableCellListNormalization";
import {
  appendMergeCellCitations,
  isMergeAxisCell,
  splitMergeTokenCell,
} from "@/utility/mergeTokenCells";
import Logger from "@/utility/logger";

// Parses markdown sections and tables from the given markdown string
export function parseMarkdownSections(markdown: string) {
  try {
    const normalizedMarkdown = normalizeMarkdownContent(markdown);

    Logger.debug("MainPage.tsx: Logging out the complete markdown:", normalizedMarkdown.substring(0, 200) + "...");
    const lines = normalizedMarkdown.trim().split("\n");

    const result = [];
    let buffer = "";
    let inTable = false;
    let currentTableLines = [];
    let previousLineWasBlank = false;
    let previousTextLineKind: "header" | "bullet" | "ordered" | "text" | null = null;

    const imageRegex = /!\[([^\]]*)\]\(([^)]+)\)/;
    const isStandaloneMarkdownRule = (value: string): boolean =>
      /^(?:\*\s*){3,}$/.test(value) || /^(?:-\s*){3,}$/.test(value);
    const isMarkdownListLine = (value: string): boolean => /^\s*(?:[-*+~]|\d+\.|\(\d+\)|[a-zA-Z]+\.)\s+/.test(value);
    const isBoldHeaderLine = (value: string): boolean =>
      /^\s*\*\*([^*\n]+?)\*\*(?:\s*(?:\[[^\]]*\]|<!--[\s\S]*?-->|\u2726|\u2727|\*))*\s*$/.test(value);
    const getTextLineKind = (value: string): "header" | "bullet" | "ordered" | "text" => {
      if (/^\s*#{1,4}\s+/.test(value) || isBoldHeaderLine(value)) {
        return "header";
      }

      if (/^(\s*)([-*+~])\s+/.test(value)) {
        return "bullet";
      }

      if (/^\s*(?:\d+\.|\(\d+\)|[a-zA-Z]+\.)\s+/.test(value)) {
        return "ordered";
      }

      return "text";
    };

    const flushBuffer = () => {
      if (buffer.length > 0) {
        result.push({ type: "text", content: buffer });
        buffer = "";
      }

      previousLineWasBlank = false;
      previousTextLineKind = null;
    };

    for (let i = 0; i < lines.length; i++) {
      const rawLine = lines[i].replace(/\r$/, "");
      const line = rawLine.trim();

      // --IMAGE DETECTION BLOCK--
      const imageMatch = line.match(imageRegex);
      if (imageMatch) {
        flushBuffer();

        result.push({
          type: "image",
          alt: imageMatch[1] || "Image",
          content: imageMatch[2],
        });
        continue;
      }
      // --IMAGE DETECTION BLOCK--

      if (line.startsWith("|")) {
        // Detected a table line
        if (!inTable) {
          // Starting a new table, flush buffered text first
          flushBuffer();
          inTable = true;
        }
        currentTableLines.push(line);
      } else if (inTable && line.length > 0 && !line.startsWith("#")) {
        // This might be a continuation of a table cell (multi-line content)
        // Append to the last table line
        if (currentTableLines.length > 0) {
          const isListContinuation = isMarkdownListLine(rawLine);
          const separator = isListContinuation ? "\n" : " ";
          const continuationText = isListContinuation ? rawLine.replace(/\s+$/, "") : line;
          currentTableLines[currentTableLines.length - 1] += separator + continuationText;
        }
      } else {
        // Non-table line or empty line that ends the table
        if (inTable) {
          // Just finished a table, parse and push it
          const parsedTable: any = parseTableFromLines(currentTableLines);
          if (parsedTable.data.length > 0) {
            result.push({
              type: "table",
              content: parsedTable.data,
              alignments: parsedTable.alignments,
              merges: parsedTable.merges,
            });
          }
          currentTableLines = [];
          inTable = false;
        }

        if (line.length === 0) {
          previousLineWasBlank = true;
          continue;
        }

        if (isStandaloneMarkdownRule(line)) {
          previousLineWasBlank = true;
          previousTextLineKind = null;
          continue;
        }

        const normalizedLine = rawLine.replace(/\s+$/, "");
        const currentLineKind = getTextLineKind(normalizedLine);

        if (buffer.length === 0) {
          buffer = normalizedLine;
        } else {
          const shouldPreserveLineBreak =
            previousLineWasBlank ||
            currentLineKind === "header" ||
            currentLineKind === "bullet" ||
            currentLineKind === "ordered" ||
            previousTextLineKind === "header";

          if (shouldPreserveLineBreak) {
            buffer += "\n" + normalizedLine;
          } else {
            buffer += " " + normalizedLine.trimStart();
          }
        }

        previousTextLineKind = currentLineKind;
        previousLineWasBlank = false;
      }
    }

    // Handle any leftover at the end
    if (inTable && currentTableLines.length > 0) {
      const parsedTable: any = parseTableFromLines(currentTableLines);
      if (parsedTable.data.length > 0) {
        result.push({
          type: "table",
          content: parsedTable.data,
          alignments: parsedTable.alignments,
          merges: parsedTable.merges,
        });
      }
    } else if (buffer.length > 0) {
      result.push({ type: "text", content: buffer });
    }

    return result;
  } catch (error) {
    Logger.error("MainPage.tsx: Error parsing string stream:", error);
    throw error;
  }
}

// Parsing the alignment row
function parseAlignmentRow(alignmentLine) {
  const trimmedLine = alignmentLine.trim();
  if (trimmedLine.startsWith("|") && trimmedLine.endsWith("|")) {
    const cells = trimmedLine.slice(1, -1).split("|");
    return cells.map((cell) => {
      const trimmedCell = cell.trim();
      const startsWithColon = trimmedCell.startsWith(":");
      const endsWithColon = trimmedCell.endsWith(":");

      if (startsWithColon && endsWithColon) {
        return "center";
      } else if (endsWithColon) {
        return "right";
      } else if (startsWithColon) {
        return "left";
      } else {
        // default means left aligned
        return "default";
      }
    });
  }
  return [];
}

// Parses table lines into a 2D array of cell contents
function parseTableFromLines(tableLines) {
  let alignments = [];

  if (tableLines.length < 2) {
    // Need at least header and one content row
    return { data: [], alignments: [], merges: [] };
  }

  // Find and remove alignment row (contains only |, -, —, –, :, and spaces)
  const alignmentRowIndex = tableLines.findIndex((line) => /^\|(?:\s*:?[-—–]+\s*:?\s*\|)+\s*$/.test(line.trim()));

  let contentLines = tableLines;
  if (alignmentRowIndex !== -1) {
    const alignmentLine = tableLines[alignmentRowIndex];
    alignments = parseAlignmentRow(alignmentLine);
    contentLines = tableLines.filter((_, index) => index !== alignmentRowIndex);
  }

  const splitTableRow = (inner: string): string[] => {
    const cells: string[] = [];
    let current = "";
    for (let ci = 0; ci < inner.length; ci++) {
      if (inner[ci] === "\\" && ci + 1 < inner.length && inner[ci + 1] === "|") {
        current += "|";
        ci++;
      } else if (inner[ci] === "|") {
        cells.push(current);
        current = "";
      } else {
        current += inner[ci];
      }
    }
    cells.push(current);
    return cells;
  };

  const rawMatrix = contentLines.map((line) => {
    const trimmedLine = line.trim();
    if (trimmedLine.startsWith("|") && trimmedLine.endsWith("|")) {
      return splitTableRow(trimmedLine.slice(1, -1)).map((cell) =>
        normalizeTableCellListContent(cell.trim().replace(/<br\s*\/?>/gi, "\n"))
      );
    } else {
      return [trimmedLine];
    }
  });

  if (alignmentRowIndex === -1 && rawMatrix.length > 0 && rawMatrix[0].length > 0) {
    const headerCellCount = rawMatrix[0].length;
    alignments = Array(headerCellCount).fill("default");
  }

  if (rawMatrix.length === 0) {
    return { data: [], alignments: [], merges: [] };
  }

  const cleanMatrix = JSON.parse(JSON.stringify(rawMatrix));

  const merges = [];

  const activeMerges = new Map();

  const rowCount = cleanMatrix.length;
  const colCount = cleanMatrix[0].length;

  for (let r = 0; r < rowCount; r++) {
    for (let c = 0; c < colCount; c++) {
      if (!cleanMatrix[r]) continue;

      const cellText = rawMatrix[r][c];
      const mergeSplit = splitMergeTokenCell(cellText);

      // Vertical Merge (^^)
      if (mergeSplit?.axis === "Y" && r > 0) {
        // Find the root row (the one that started this merge)
        let rootRow = r - 1;
        // Walk up while the cell above is also a merge token
        while (rootRow > 0 && isMergeAxisCell(rawMatrix[rootRow][c], "Y")) {
          rootRow--;
        }

        // Unique key for this merge group
        const key = `V:${rootRow},${c}`;
        let mergeDef = activeMerges.get(key);

        if (!mergeDef) {
          // New merge detected
          mergeDef = { row: rootRow, col: c, rowSpan: 1, colSpan: 1 };
          activeMerges.set(key, mergeDef);
          merges.push(mergeDef);
        }

        mergeDef.rowSpan += 1;
        // Re-attach any citations that rode the merge cell onto the anchor.
        cleanMatrix[rootRow][c] = appendMergeCellCitations(cleanMatrix[rootRow][c], mergeSplit.citations);
        // Clear the token so it doesn't print
        cleanMatrix[r][c] = "";
      }

      // Horizontal Merge (>>)
      else if (mergeSplit?.axis === "X" && c > 0) {
        // Find the root col
        let rootCol = c - 1;
        while (rootCol > 0 && isMergeAxisCell(rawMatrix[r][rootCol], "X")) {
          rootCol--;
        }

        const key = `H:${r},${rootCol}`;
        let mergeDef = activeMerges.get(key);

        if (!mergeDef) {
          mergeDef = { row: r, col: rootCol, rowSpan: 1, colSpan: 1 };
          activeMerges.set(key, mergeDef);
          merges.push(mergeDef);
        }

        mergeDef.colSpan += 1;
        // Re-attach any citations that rode the merge cell onto the anchor.
        cleanMatrix[r][rootCol] = appendMergeCellCitations(cleanMatrix[r][rootCol], mergeSplit.citations);
        // Clear the token so it doesn't print
        cleanMatrix[r][c] = "";
      }
    }
  }

  return { data: cleanMatrix, alignments, merges };
}
